import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import {
  ITEM_DATE_KEYS,
  isIsoDate,
  type Properties,
} from "@egalregal/shared";
import { useT } from "../i18n";
import { RemoveIcon } from "./icons";
import { datePropertyLabel } from "./Deadlines";

export type DatePropertyValues = Partial<Record<string, string>>;

/**
 * Read the editable date values off an entity's property bag. Only the closed
 * UI vocabulary (`keys`) is exposed; unknown keys stay untouched in the bag.
 */
export function dateValuesOf(
  properties: Properties | undefined,
  keys: readonly string[] = ITEM_DATE_KEYS,
): DatePropertyValues {
  const values: DatePropertyValues = {};
  for (const key of keys) {
    const property = properties?.[key];
    if (property?.type === "date") values[key] = property.value;
  }
  return values;
}

/**
 * Build the property bag a PATCH/POST carries from the form's date values.
 * Empty fields are dropped, so clearing a date removes the property. Invalid
 * values are dropped too — the API rejects them regardless, and the native
 * date input only produces valid days.
 */
export function datePropertiesOf(
  values: DatePropertyValues,
  keys: readonly string[] = ITEM_DATE_KEYS,
): Properties {
  const properties: Properties = {};
  for (const key of keys) {
    const value = values[key]?.trim();
    if (value && isIsoDate(value)) properties[key] = { type: "date", value };
  }
  return properties;
}

/**
 * Merge the edited vocabulary back into an existing bag while preserving
 * unknown property keys read-only (ADR-0037): an edit must never drop a
 * property the closed UI does not know about.
 */
export function mergeDateProperties(
  existing: Properties | undefined,
  values: DatePropertyValues,
  keys: readonly string[] = ITEM_DATE_KEYS,
): Properties {
  const known = new Set<string>(keys);
  const preserved: Properties = {};
  for (const [key, property] of Object.entries(existing ?? {})) {
    if (!known.has(key)) preserved[key] = property;
  }
  return { ...preserved, ...datePropertiesOf(values, keys) };
}

/**
 * One validated, clearable day-precision date input per configured key — the
 * five item semantics by default, container expires-on by `keys`. Day
 * precision everywhere: the browser date picker plus shared validation.
 */
export function DatePropertyFields({
  values,
  onChange,
  keys = ITEM_DATE_KEYS,
}: {
  values: DatePropertyValues;
  onChange: (values: DatePropertyValues) => void;
  keys?: readonly string[];
}) {
  const t = useT();
  return (
    <Stack spacing={1.5}>
      {keys.map((key) => {
        const label = datePropertyLabel(t, key);
        const value = values[key] ?? "";
        return (
          <TextField
            key={key}
            type="date"
            size="small"
            fullWidth
            label={label}
            value={value}
            onChange={(event) => onChange({ ...values, [key]: event.target.value })}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { "aria-label": label },
              input: {
                endAdornment: value ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={`${t("clearDate")}: ${label}`}
                      onClick={() => onChange({ ...values, [key]: "" })}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />
        );
      })}
    </Stack>
  );
}
